'use client';
import { API_BASES } from '../../config/constants';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  Stack,
  Chip,
  IconButton,
  Paper,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Checkbox
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import LZString from 'lz-string';
import MapTwoToneIcon from '@mui/icons-material/MapTwoTone';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { baseMaps } from '../../utils/mapConfig';
import { timezone } from '../../utils/timeZone';

import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LayersIcon from '@mui/icons-material/Layers';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import MemoryIcon from '@mui/icons-material/Memory';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import SpeedIcon from '@mui/icons-material/Speed';

import PurpleCar from 'assets/images/icons/tracking-truck.webp';
import GreenCar from 'assets/images/icons/moving-truck.webp';
import RedCar from 'assets/images/icons/parking-truck.webp';
import YellowCar from 'assets/images/icons/idle-truck.webp';
import BlackCar from 'assets/images/icons/offline-truck.webp';

const DEFAULT_ICON = BlackCar;

const getVehicleIcon = (v) => {
  if (!v) return DEFAULT_ICON;

  const status = (v.status || v.info?.status_text || '').toLowerCase();
  const gpsStatus = (v.gps || v.info?.gps || '').toLowerCase();
  const engine = String(v.engine || v.engine_status || v.info?.engine_status || '').toUpperCase();
  const speed = Number(v.speed || v.info?.speed || 0);

  const spatialId = Number(v.raw?.spatial_id || v.spatial_id || v.info?.spatial_id || 0);
  const spatialMaxSpeed = Number(v.raw?.spatial_max_speed || v.spatial_max_speed || v.info?.spatial_max_speed || 0);
  const isLoaded = v.raw?.is_in_retase !== false && v.is_in_retase !== false && v.info?.is_in_retase !== false;
  const isOverSpeedSpatial = spatialId > 0 && spatialMaxSpeed > 0 && speed > spatialMaxSpeed && isLoaded;
  const isGpsUpdated = gpsStatus.includes('updated') || gpsStatus.includes('update');
  const speedMsg = String(v.raw?.speed_message || v.speed_message || v.info?.speed_message || '').toLowerCase();
  const eventName = String(v.raw?.event || v.event || v.info?.alarm_type || '').toLowerCase();
  const limitSpeed = Number(v.vehicle?.max_speed || v.info?.max_speed || 70);
  const isOverSpeedMessage = speedMsg.includes('over') || eventName.includes('overspeed');
  const isOverSpeedLimit = engine === 'ON' && speed > limitSpeed;
  const isStatusOverSpeed = status.includes('over speed') || isOverSpeedMessage || isOverSpeedLimit;

  const isAtPolygon =
    status.includes('at pool') || status.includes('at geofence') || status.includes('at customer') || status.includes('at polygon');
  const isAtTrip = status === 'at trip';

  if (status.includes('delay') || gpsStatus.includes('delay')) return RedCar;

  if (isAtTrip && engine === 'ON' && speed > 0 && isGpsUpdated) return GreenCar;
  if (isAtPolygon && engine === 'ON' && speed > 0 && isGpsUpdated) return GreenCar;
  if (isOverSpeedSpatial && isStatusOverSpeed && engine === 'ON' && speed > 0 && isGpsUpdated) return GreenCar;

  if ((isAtTrip || isAtPolygon) && engine === 'ON' && speed === 0 && isGpsUpdated) return YellowCar;

  if (isAtTrip && engine === 'OFF') return BlackCar;
  if (isAtPolygon && speed === 0 && isGpsUpdated) return PurpleCar;

  if (engine === 'OFF' || status.includes('offline')) return BlackCar;

  if (engine === 'ON' && speed > 0 && isGpsUpdated) return GreenCar;
  if (engine === 'ON' && speed === 0 && isGpsUpdated) return YellowCar;
  if (engine === 'IDLE' && speed === 0 && isGpsUpdated) return YellowCar;

  return DEFAULT_ICON;
};

const getStatusLabel = (payloadData) => {
  if (!payloadData) return '-';
  const status = String(payloadData.status || '').toLowerCase();
  const gpsStatus = String(payloadData.gps || payloadData.info?.gps || '').toLowerCase();
  const engine = String(payloadData.engine_status || payloadData.engine || '').toUpperCase();
  const speed = Number(payloadData.speed || 0);

  const spatial_id = Number(payloadData.spatial_id || payloadData.info?.spatial_id || 0);
  const spatial_max_speed = Number(payloadData.spatial_max_speed || payloadData.info?.spatial_max_speed || 0);
  const spatial_min_speed = Number(payloadData.spatial_min_speed || payloadData.info?.spatial_min_speed || 0);
  const isLoaded =
    payloadData.raw?.is_in_retase !== false && payloadData.is_in_retase !== false && payloadData.info?.is_in_retase !== false;

  const isAtPolygon =
    status.includes('at pool') || status.includes('at geofence') || status.includes('at customer') || status.includes('at polygon');
  const isOverSpeedSpatial = spatial_id > 0 && spatial_max_speed > 0 && speed > spatial_max_speed && isLoaded;
  const isUnderSpeedSpatial = spatial_id > 0 && spatial_min_speed > 0 && speed < spatial_min_speed && isLoaded;
  const isStatusOverSpeed = status.includes('over speed') || status.includes('overspeed');
  const isStatusUnderSpeed = status.includes('under speed') || status.includes('underspeed');
  const isGpsUpdated = gpsStatus.includes('updated') || gpsStatus.includes('update');

  if ((isStatusOverSpeed || isOverSpeedSpatial) && isGpsUpdated) return 'Moving';
  if ((isStatusUnderSpeed || isUnderSpeedSpatial) && isGpsUpdated) return 'Moving';

  if (gpsStatus.includes('delay')) {
    if (gpsStatus.includes('24')) return 'Delay > 24h';
    if (gpsStatus.includes('1')) return 'Delay > 1h';
    return 'Delay';
  }
  if (status.includes('delay')) {
    if (status.includes('24')) return 'Delay > 24h';
    if (status.includes('1')) return 'Delay > 1h';
    return 'Delay';
  }
  if (status.includes('maintenance')) return 'Maintenance';

  if ((engine === 'ON' || engine === 'IDLE') && speed > 0 && isGpsUpdated) return 'Moving';
  if ((engine === 'ON' || engine === 'IDLE') && speed === 0 && isGpsUpdated) return 'Idle';

  if (engine === 'OFF') {
    if (isAtPolygon) return 'Parked';
    return 'Stopped';
  }

  if (speed > 0 && isGpsUpdated) return 'Moving';
  if (isAtPolygon) return 'Parked';

  return 'Stopped';
};

const getEngineLabel = (engine) => {
  if (!engine) return '-';
  const e = engine.toUpperCase();
  if (e === 'IDLE') return 'Idle';
  if (e === 'OFFLINE') return 'Offline';
  return engine;
};

const getStatusColor = (payloadData) => {
  if (!payloadData) return 'default';
  const status = String(payloadData.status || '').toLowerCase();
  const gpsStatus = String(payloadData.gps || payloadData.info?.gps || '').toLowerCase();
  const engine = String(payloadData.engine_status || payloadData.engine || '').toUpperCase();
  const speed = Number(payloadData.speed || 0);

  const spatial_id = Number(payloadData.spatial_id || payloadData.info?.spatial_id || 0);
  const spatial_max_speed = Number(payloadData.spatial_max_speed || payloadData.info?.spatial_max_speed || 0);
  const spatial_min_speed = Number(payloadData.spatial_min_speed || payloadData.info?.spatial_min_speed || 0);
  const isLoaded =
    payloadData.raw?.is_in_retase !== false && payloadData.is_in_retase !== false && payloadData.info?.is_in_retase !== false;

  const isAtPolygon =
    status.includes('at pool') || status.includes('at geofence') || status.includes('at customer') || status.includes('at polygon');
  const isOverSpeedSpatial = spatial_id > 0 && spatial_max_speed > 0 && speed > spatial_max_speed && isLoaded;
  const isUnderSpeedSpatial = spatial_id > 0 && spatial_min_speed > 0 && speed < spatial_min_speed && isLoaded;
  const isStatusOverSpeed = status.includes('over speed') || status.includes('overspeed');
  const isStatusUnderSpeed = status.includes('under speed') || status.includes('underspeed');
  const isGpsUpdated = gpsStatus.includes('updated') || gpsStatus.includes('update');

  if ((isStatusOverSpeed || isOverSpeedSpatial) && isGpsUpdated) return 'success';
  if ((isStatusUnderSpeed || isUnderSpeedSpatial) && isGpsUpdated) return 'success';

  if (status.includes('delay') || gpsStatus.includes('delay')) return 'error';
  if (status.includes('maintenance')) return 'error';

  if ((engine === 'ON' || engine === 'IDLE') && speed > 0 && isGpsUpdated) return 'success';
  if ((engine === 'ON' || engine === 'IDLE') && speed === 0 && isGpsUpdated) return 'warning';

  if (engine === 'OFF') {
    if (isAtPolygon) return 'primary';
    return 'default';
  }

  if (speed > 0 && isGpsUpdated) return 'success';
  if (isAtPolygon) return 'primary';

  return 'default';
};

const formatDate = (isoString) => {
  if (!isoString || isoString === '-') return '-';
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch {
    return isoString;
  }
};

// Fungsi pengecekan matematis Ray-Casting untuk menentukan point dalam polygon
const isPointInPolygon = (latlng, poly) => {
  if (!poly || typeof poly.getLatLngs !== 'function') return false;
  const lat = latlng[0];
  const lng = latlng[1];
  const coords = poly.getLatLngs()[0];

  let isInside = false;
  for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
    const xi = coords[i].lat,
      yi = coords[i].lng;
    const xj = coords[j].lat,
      yj = coords[j].lng;
    const intersect = yi > lng !== yj > lng && lat < ((xj - xi) * (lng - yi)) / (yj - yi) + xi;
    if (intersect) isInside = !isInside;
  }
  return isInside;
};

const fetchCurrentVehicleStatus = async (apiKey, licensePlate, tz = null) => {
  try {
    const tzVal = tz !== null ? tz : typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
    const url = `${API_BASES.FLEET_API}/open-api/current-vehicle-status?tz=${tzVal}&apiKey=${apiKey}&licensePlate=${encodeURIComponent(licensePlate)}`;

    const response = await fetch(url, { method: 'GET' });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Gagal mengambil data update kendaraan:', error);
    return null;
  }
};

const getRotatedTruckIcon = (v) => {
  const angle = v.angle ?? v.raw?.angle ?? v.direction ?? v.info?.direction ?? v.info?.angle ?? null;
  const rotation = Number(angle || 0) - 90;

  const iconUrl = getVehicleIcon(v);
  const truckSrc = typeof iconUrl === 'string' ? iconUrl : iconUrl?.src || iconUrl;

  const spatialId = Number(v.raw?.spatial_id || v.spatial_id || v.info?.spatial_id || 0);
  const spatialMaxSpeed = Number(v.raw?.spatial_max_speed || v.spatial_max_speed || v.info?.spatial_max_speed || 0);
  const spatialMinSpeed = Number(v.raw?.spatial_min_speed || v.spatial_min_speed || v.info?.spatial_min_speed || 0);
  const speed = Number(v.raw?.speed || v.speed || v.info?.speed || 0);
  const speedMsg = String(v.raw?.speed_message || v.speed_message || v.info?.speed_message || '').toLowerCase();
  const eventName = String(v.raw?.event || v.event || v.info?.alarm_type || '').toLowerCase();
  const harshEvent = String(v.raw?.harsh_event || v.harsh_event || v.info?.harsh_event || '').toLowerCase();
  const engineStatus = String(
    v.raw?.engine || v.engine || v.raw?.engine_status || v.engine_status || v.info?.engine_status || ''
  ).toUpperCase();
  const limitSpeed = Number(v.vehicle?.max_speed || v.info?.max_speed || 70);
  const polygonTypeName =
    v.raw?.polygon_type_name || v.polygon_type_name || v.info?.polygon_type_name || v.raw?.spatial_description || 'Geofence';
  const status = String(v.status || v.info?.status_text || '').toLowerCase();

  const isHarshBraking =
    harshEvent.includes('braking') ||
    harshEvent.includes('breaking') ||
    speedMsg.includes('harsh braking') ||
    eventName.includes('harsh braking');
  const isHarshAccel =
    harshEvent.includes('acceleration') || speedMsg.includes('harsh acceleration') || eventName.includes('harsh acceleration');
  const isHarshCorner = harshEvent.includes('cornering') || speedMsg.includes('harsh cornering') || eventName.includes('harsh cornering');

  const isOverSpeedMessage = speedMsg.includes('over') || eventName.includes('overspeed');
  const isOverSpeedLimit = engineStatus === 'ON' && speed > limitSpeed;
  const isOverSpeed = isOverSpeedMessage || isOverSpeedLimit;

  const isAtPolygon =
    status.includes('at pool') || status.includes('at geofence') || status.includes('at customer') || status.includes('at polygon');
  const isAtTrip = status === 'at trip';
  const isLoaded = v.raw?.is_in_retase !== false && v.is_in_retase !== false && v.info?.is_in_retase !== false;

  const isOverSpeedSpatial = spatialId > 0 && spatialMaxSpeed > 0 && speed > spatialMaxSpeed && isLoaded;
  const isUnderSpeedSpatial = spatialId > 0 && spatialMinSpeed > 0 && speed > 0 && speed < spatialMinSpeed && isLoaded;

  const isMoving =
    engineStatus === 'ON' &&
    speed > 0 &&
    (isAtTrip || isAtPolygon || isOverSpeedSpatial || isUnderSpeedSpatial || isOverSpeed) &&
    !status.includes('delay');

  let glowClass = '';
  let pulseReason = null;
  let pulseColor = null;

  if (isMoving) {
    if (isHarshBraking || isHarshAccel || isHarshCorner) {
      glowClass = 'glow-overspeed';
      const eventTitle = isHarshBraking ? 'Harsh Braking' : isHarshAccel ? 'Harsh Acceleration' : 'Harsh Cornering';
      pulseReason = `Harsh Event: ${eventTitle}`;
      pulseColor = 'error';
    } else if (isOverSpeedSpatial) {
      glowClass = 'glow-overspeed-spatial';
      pulseReason = `${v.fleet_name || v.vehicle_license_plate || v.license_plate || 'Unknown'} Over speed ${speed} in location ${polygonTypeName}, Spatial Max speed : ${spatialMaxSpeed}`;
      pulseColor = 'warning';
    } else if (isUnderSpeedSpatial) {
      glowClass = 'glow-idle';
      pulseReason = `${v.fleet_name || v.vehicle_license_plate || v.license_plate || 'Unknown'} Under speed ${speed} in location ${polygonTypeName}, Spatial Min speed : ${spatialMinSpeed}`;
      pulseColor = 'warning';
    } else if (isOverSpeed) {
      glowClass = 'glow-overspeed';
      pulseReason = `Overspeed Warning`;
      pulseColor = 'error';
    } else if (engineStatus === 'IDLE') {
      glowClass = 'glow-idle';
      pulseReason = `Engine IDLE`;
      pulseColor = 'warning';
    }
  }

  return {
    icon: L.divIcon({
      className: 'custom-rotated-truck',
      html: `
        <div style="width: 100%; height: 100%; display: flex; justify-content: center; align-items: center; position: relative;">
          <div class="pulse-ring ${glowClass}"></div>
          <img 
            src="${truckSrc}" 
            style="
              width: 50px; 
              height: 25px; 
              transform: rotate(${rotation}deg); 
              transform-origin: center center;
              transition: transform 0.5s ease-out;
              drop-shadow: 0 4px 6px rgba(0,0,0,0.3);
              z-index: 2;
              position: relative;
            " 
          />
        </div>
      `,
      iconSize: [50, 25],
      iconAnchor: [25, 12]
    }),
    pulseReason,
    pulseColor
  };
};
const fetchPolygonData = async (endpoint, token, companyId, locationType) => {
  try {
    const url = `${API_BASES.FLEET_API}${endpoint}`;
    const columns = [];
    if (companyId) {
      const idStr = String(companyId);
      columns.push({ logic_operator: 'in', name: 'company_id', operator: idStr.includes(',') ? 'AND' : 'AND', value: idStr });
    }
    const payload = { paging: { start: 0, length: 1000 }, columns, joins: [{ name: 'company', column_results: ['name'] }] };

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`Failed to fetch ${locationType}`);
    const resJson = await response.json();
    const batchData = Array.isArray(resJson?.data) ? resJson.data : Array.isArray(resJson) ? resJson : [];
    const activeData = batchData.filter(
      (item) => item.status !== false && item.status !== 0 && String(item.status).toLowerCase() !== 'false'
    );
    return activeData.map((item) => ({ ...item, location_type: locationType }));
  } catch (error) {
    console.error(error);
    return [];
  }
};

const fetchAllLocationsPublic = async (token, companyId) => {
  try {
    const results = await Promise.all([
      fetchPolygonData('/pool/search', token, companyId, 'PIT'),
      fetchPolygonData('/transit-point/search', token, companyId, 'Jetty'),
      fetchPolygonData('/geofence/search', token, companyId, 'Geofence library')
    ]);
    return results.flat();
  } catch (error) {
    console.error('Error fetching all locations:', error);
    return [];
  }
};
export default function SharedLiveVehiclePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerInstanceRef = useRef(null);
  const locationLayerRef = useRef(null); // Ref untuk nyimpan layer polygon
  const currentAreaIdRef = useRef(null); // Ref agar popup tidak spam saat stay di dalam area

  const [isLoading, setIsLoading] = useState(true);
  const [activeLocationPolygon, setActiveLocationPolygon] = useState(null);
  const [locPopoverOpen, setLocPopoverOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isExpired, setIsExpired] = useState(false);

  const [payloadData, setPayloadData] = useState(null);
  const [showCard, setShowCard] = useState(false);
  const [cardPosition, setCardPosition] = useState({ top: 0, left: 0 });
  const [isLegendOpen, setIsLegendOpen] = useState(false);

  const [isLayerMenuOpen, setIsLayerMenuOpen] = useState(false);
  const [activeBaseMap, setActiveBaseMap] = useState('Satelite Map');

  const [polygonLocations, setPolygonLocations] = useState([]); // State simpan data polygon
  const [activePolygonName, setActivePolygonName] = useState(null);
  const [isPolygonSidebarOpen, setIsPolygonSidebarOpen] = useState(true);
  const [polygonSearch, setPolygonSearch] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState([]);

  const availableCompanies = useMemo(() => {
    const companiesMap = {};
    polygonLocations.forEach((loc) => {
      const lookupId = loc.company_id || loc.company;
      if (lookupId) {
        const label = loc.join?.company_name || loc.company || `Company ${lookupId}`;
        companiesMap[lookupId] = label;
      }
    });
    return Object.entries(companiesMap).map(([id, label]) => ({ id, label }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polygonLocations, payloadData]);

  useEffect(() => {
    if (availableCompanies.length > 0 && selectedCompanies.length === 0) {
      setSelectedCompanies(availableCompanies.map((c) => c.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCompanies]);

  const handlePolygonClick = useCallback((loc) => {
    const map = mapInstanceRef.current;
    const locLayer = locationLayerRef.current;
    if (!map || !locLayer) return;

    let targetLayer = null;
    locLayer.eachLayer((layer) => {
      if (layer.options.data && layer.options.data.id === loc.id) {
        targetLayer = layer;
      }
    });

    if (targetLayer) {
      const center = targetLayer.getBounds().getCenter();
      map.flyTo(center, 16, { animate: true });
      targetLayer.openPopup();
      setActiveLocationPolygon({ layer: targetLayer, data: loc });
      setLocPopoverOpen(true);
    }
  }, []);

  // 1. DECODE URL DAN FETCH POLYGON AWAL
  useEffect(() => {
    const initializeData = async () => {
      const searchParams = new URLSearchParams(location.search);
      const sharingCode = searchParams.get('data');

      if (!sharingCode) {
        setErrorMsg('Link pelacakan tidak valid atau tidak lengkap.');
        setIsLoading(false);
        return;
      }

      try {
        let encodedData = sharingCode;
        const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
        if (sharingCode.includes('-')) {
          const res = await fetch(`${API_BASES.FLEET_API}/share-link-bycode?tz=${tz}&sharingCode=${sharingCode}`);
          if (!res.ok) throw new Error('Sharing code tidak valid atau kadaluarsa');
          const jsonRes = await res.json();
          encodedData = jsonRes?.data?.encode_base64;
          if (!encodedData) throw new Error('Data encode_base64 tidak ditemukan');
        }

        const decompressed = LZString.decompressFromEncodedURIComponent(encodedData);

        if (!decompressed) {
          throw new Error('Gagal decode data (mungkin corrupt)');
        }

        const parsedData = JSON.parse(decompressed);

        if (parsedData.exp && Date.now() > parsedData.exp) {
          setIsExpired(true);
          setIsLoading(false);
          return;
        }

        const lp = parsedData.lp || parsedData.fleet_name || parsedData.license_plate;
        const tk = parsedData.tk || parsedData.token || parsedData.apiKey;
        const ak = parsedData.ak || parsedData.apiKey || tk;
        const cid = parsedData.cid || parsedData.companyId || '';

        if (!lp || !ak) throw new Error('Data tidak lengkap (plat/api-key hilang)');

        // FETCH INIT DATA
        const initialStatusResponse = await fetchCurrentVehicleStatus(ak, lp, tz);
        const initialData = initialStatusResponse?.data || {};

        if (!initialData.lat || !initialData.lon) {
          throw new Error('Gagal mendapatkan lokasi awal kendaraan');
        }

        const spatialId = Number(initialData.spatial_id || initialData.info?.spatial_id || 0);
        const spatialMaxSpeed = Number(initialData.spatial_max_speed || initialData.info?.spatial_max_speed || 0);
        const speedNum = Number(initialData.speed || 0);
        const polygonTypeName =
          initialData.polygon_type_name || initialData.info?.polygon_type_name || initialData.spatial_description || 'Geofence';
        let polygonOverspeedText = null;
        if (spatialId > 0 && spatialMaxSpeed > 0 && speedNum > spatialMaxSpeed) {
          polygonOverspeedText = `Over speed di ${polygonTypeName} (Max: ${spatialMaxSpeed} Km/H)`;
        }

        const mergedData = {
          ...initialData,
          lat: initialData.lat,
          lon: initialData.lon,
          angle: initialData.angle || initialData.direction,
          config: parsedData.cfg || parsedData.config,
          exp: parsedData.exp || parsedData.expiresAt,
          fleet_name: parsedData.fn,
          vehicle_type: parsedData.vt,
          imei: parsedData.im,
          vin: initialData.info?.chasis_no || parsedData.vn,
          driver_name: initialData.driver_name || parsedData.dn,
          status: initialData.status,
          engine_status: initialData.engine,
          harsh_event: initialData.harsh_event || initialData.info?.harsh_event || null,
          last_location_time: initialData.last_update,
          last_connected_time: initialData.last_update,
          odo: Number(initialData.current_odo).toFixed(2),
          stop_duration: initialData.stop_duration || '-',
          distance_traveled_km: initialData.info?.total_distance,
          fuel_liter: initialData.fuel,
          batt_volt: initialData.batt_volt,
          canbusData: initialData.canbus,
          cns: parsedData.cns || {},
          polygonOverspeedText,
          lp,
          ak,
          tk,
          cid
        };

        const { pulseReason, pulseColor } = getRotatedTruckIcon(mergedData);
        mergedData.pulseReason = pulseReason;
        mergedData.pulseColor = pulseColor;

        setPayloadData(mergedData);

        if (tk) {
          const locs = await fetchAllLocationsPublic(tk, cid);
          setPolygonLocations(locs);
        }

        setIsLoading(false);
        setShowCard(true);
      } catch (err) {
        console.error('Decode error:', err);
        setErrorMsg('Data link rusak, kadaluarsa, atau terpotong saat disalin.');
        setIsLoading(false);
      }
    };
    initializeData();
  }, [location]);

  const updateCardPosition = () => {
    if (!mapInstanceRef.current || !payloadData || !markerInstanceRef.current) return;
    const map = mapInstanceRef.current;

    // Ambil kordinat dari marker yg bergerak, BUKAN dari awal payload load, agar popup ngikutin mobil
    const currentLatLng = markerInstanceRef.current.getLatLng();
    const containerPoint = map.latLngToContainerPoint(currentLatLng);

    setCardPosition({
      top: containerPoint.y - 20,
      left: containerPoint.x
    });
  };

  // 2. INIT MAP & DRAW POLYGONS
  useEffect(() => {
    if (isLoading || errorMsg || isExpired || !payloadData || !mapContainerRef.current) return;

    if (!mapInstanceRef.current) {
      const { lat, lon } = payloadData;

      let styleTag = document.getElementById('glow-blink-style');
      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = 'glow-blink-style';
        styleTag.innerHTML = `
          @keyframes pulseRingRed {
            0% { transform: scale(0.8); opacity: 0.8; }
            100% { transform: scale(2.5); opacity: 0; }
          }
          @keyframes pulseRingYellow {
            0% { transform: scale(0.8); opacity: 0.8; }
            100% { transform: scale(2.5); opacity: 0; }
          }
          .pulse-ring {
            position: absolute;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            z-index: 1;
            display: none;
          }
          .pulse-ring.glow-overspeed {
            display: block;
            background-color: rgba(244, 67, 54, 0.6);
            box-shadow: 0 0 10px rgba(244, 67, 54, 0.8);
            animation: pulseRingRed 1s infinite ease-out;
          }
          @keyframes pulseRingOrange {
            0% { transform: scale(0.8); opacity: 0.8; }
            100% { transform: scale(2.5); opacity: 0; }
          }
          .pulse-ring.glow-overspeed-spatial {
            display: block;
            background-color: rgba(243, 147, 153, 0.6);
            box-shadow: 0 0 10px rgba(243, 147, 153, 0.8);
            animation: pulseRingOrange 1s infinite ease-out;
          }
          .pulse-ring.glow-idle {
            display: block;
            background-color: rgba(255, 193, 7, 0.6);
            box-shadow: 0 0 10px rgba(255, 193, 7, 0.8);
            animation: pulseRingYellow 1.5s infinite ease-out;
          }
          .polygon-glow-active {
            stroke: var(--glow-color, #ffffff) !important;
            stroke-width: 4px !important;
            stroke-dasharray: 6 !important;
            animation: polygon-march 1s linear infinite;
            filter: drop-shadow(0px 0px 4px var(--glow-color, #ffffff)) brightness(1.5) !important;
          }
          @keyframes polygon-march {
            to { stroke-dashoffset: -12; }
          }
        `;
        document.head.appendChild(styleTag);
      }

      const map = L.map(mapContainerRef.current, {
        center: [lat, lon],
        zoom: 16,
        zoomControl: true,
        attributionControl: false
      });

      const defaultLayer = baseMaps['Satelite Map'] || Object.values(baseMaps)[0];
      defaultLayer.addTo(map);

      locationLayerRef.current = L.featureGroup().addTo(map);

      const { icon: rotatedIcon } = getRotatedTruckIcon(payloadData);
      const marker = L.marker([lat, lon], { icon: rotatedIcon }).addTo(map);

      marker.on('click', () => {
        setShowCard(true);
        map.panTo(marker.getLatLng(), { animate: true });
      });

      map.on('move', updateCardPosition);
      map.on('zoom', updateCardPosition);
      map.on('dragstart', () => {
        setLocPopoverOpen(false);
      });

      mapInstanceRef.current = map;
      markerInstanceRef.current = marker;

      setTimeout(() => {
        updateCardPosition();
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, errorMsg, isExpired, payloadData]);

  // 3. EFFECT KHUSUS UNTUK RENDER POLYGON KE PETA
  useEffect(() => {
    const map = mapInstanceRef.current;
    const locLayer = locationLayerRef.current;
    if (!map || !locLayer || polygonLocations.length === 0) return;

    locLayer.clearLayers();

    const filteredForMap = polygonLocations.filter((loc) => {
      const lookupId = loc.company_id || loc.company;
      if (!lookupId) return true;
      if (selectedCompanies.length === 0) return true;
      return selectedCompanies.includes(String(lookupId));
    });

    filteredForMap.forEach((loc) => {
      if (loc.geom) {
        try {
          const wkt = loc.geom;
          const coordsStr = wkt.replace('POLYGON((', '').replace('))', '').replace('POLYGON ((', '');
          const pairs = coordsStr.split(',');
          const polyCoords = pairs.map((pair) => {
            const [lng, ptLat] = pair.trim().split(' ');
            return [Number(ptLat), Number(lng)];
          });

          if (polyCoords.length > 0) {
            // WARNA POLYGON SESUAI TIPE
            let typeColor = '#7b1fa2'; // Default Ungu (Geofence)
            if (loc.location_type === 'PIT')
              typeColor = '#1565c0'; // Biru
            else if (loc.location_type === 'Jetty') typeColor = '#2e7d32'; // Hijau

            const polygon = L.polygon(polyCoords, { color: typeColor, weight: 2, fillOpacity: 0.15, data: loc });

            // KLIK POLYGON MUNCUL POPUP NAMA & TIPE
            polygon.on('click', (e) => {
              L.DomEvent.stopPropagation(e);
              setActiveLocationPolygon({ layer: polygon, data: loc });
              setLocPopoverOpen(true);
            });

            locLayer.addLayer(polygon);
          }
        } catch (e) {
          console.warn('WKT Parse error', e);
        }
      }
    });
  }, [polygonLocations, selectedCompanies, payloadData]);

  // 4. FUNGSI CEK INTERSEKSI MOBIL MASUK POLYGON
  const checkPolygonIntersection = useCallback((currentLocArray) => {
    if (!locationLayerRef.current || !currentLocArray || !markerInstanceRef.current) return;
    let foundArea = null;

    locationLayerRef.current.eachLayer((layer) => {
      if (layer instanceof L.Polygon && isPointInPolygon(currentLocArray, layer)) {
        foundArea = layer.options.data;
      }
    });

    if (foundArea) {
      const areaId = foundArea.id || foundArea.pool_name || foundArea.geofence_name;
      // Hanya munculkan alert kalau baru pindah masuk area baru (Biar gak spam)
      if (currentAreaIdRef.current !== areaId) {
        currentAreaIdRef.current = areaId;
        const name = foundArea.name || foundArea.pool_name || foundArea.geofence_name || 'Area';
        const typeLabel = foundArea.location_type === 'Geofence library' ? 'Geofence' : foundArea.location_type;
        setActivePolygonName(`${name} (${typeLabel})`);
      }
    } else {
      currentAreaIdRef.current = null;
      setActivePolygonName(null);
    }
  }, []);

  useEffect(() => {
    if (!payloadData || isLoading || isExpired || !mapInstanceRef.current) return;

    const licensePlate = payloadData.lp || payloadData.fleet_name || payloadData.vehicle_license_plate || payloadData.license_plate;
    if (!licensePlate) return;

    let intervalId;

    const doPoll = async () => {
      const apiKeyPublic = payloadData.ak || payloadData.apiKey;
      if (!apiKeyPublic) return;

      const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
      const responseData = await fetchCurrentVehicleStatus(apiKeyPublic, licensePlate, tz);

      if (responseData && responseData.data) {
        const newData = responseData.data;

        const spatialId = Number(newData.spatial_id || newData.info?.spatial_id || 0);
        const spatialMaxSpeed = Number(newData.spatial_max_speed || newData.info?.spatial_max_speed || 0);
        const spatialMinSpeed = Number(newData.spatial_min_speed || newData.info?.spatial_min_speed || 0);
        const speedNum = Number(newData.speed || 0);
        const polygonTypeName = newData.polygon_type_name || newData.info?.polygon_type_name || newData.spatial_description || 'Geofence';
        let polygonSpeedAlertText = null;
        if (newData.harsh_event || newData.info?.harsh_event) {
          polygonSpeedAlertText = null;
        } else if (spatialId > 0 && spatialMaxSpeed > 0 && speedNum > spatialMaxSpeed) {
          polygonSpeedAlertText = `Over speed di ${polygonTypeName} (Max: ${spatialMaxSpeed} Km/H)`;
        } else if (spatialId > 0 && spatialMinSpeed > 0 && speedNum > 0 && speedNum < spatialMinSpeed) {
          polygonSpeedAlertText = `Under speed di ${polygonTypeName} (Min: ${spatialMinSpeed} Km/H)`;
        }

        setPayloadData((prevData) => {
          if (!prevData) return prevData;
          const updatedData = {
            ...prevData,
            ...newData,
            lat: newData.lat,
            lon: newData.lon,
            angle: newData.angle || newData.direction,
            status: newData.status,
            engine_status: newData.engine,
            harsh_event: newData.harsh_event || newData.info?.harsh_event || prevData.harsh_event || null,
            last_location_time: newData.last_update,
            last_connected_time: newData.last_update,
            odo: Number(newData.current_odo).toFixed(2),
            stop_duration: newData.stop_duration || '-',
            distance_traveled_km: newData.info?.total_distance || prevData.distance_traveled_km,
            fuel_liter: newData.fuel,
            batt_volt: newData.batt_volt,
            fleet_name: newData.vehicle_license_plate || prevData.fleet_name,
            vehicle_type: newData.vehicle_type_name || prevData.vehicle_type,
            imei: newData.imei_no || prevData.imei,
            driver_name: newData.driver_name || prevData.driver_name,
            vin: newData.info?.chasis_no || prevData.vin,
            canbusData: {
              ...prevData.canbusData,
              ...newData.canbus
            },
            polygonOverspeedText: polygonSpeedAlertText,
            config: prevData.config,
            expiresAt: prevData.expiresAt,
            id: prevData.id
          };

          const { icon: rotatedIcon, pulseReason, pulseColor } = getRotatedTruckIcon(updatedData);
          if (markerInstanceRef.current) {
            markerInstanceRef.current.setIcon(rotatedIcon);
          }

          return { ...updatedData, pulseReason, pulseColor };
        });
        const map = mapInstanceRef.current;
        const marker = markerInstanceRef.current;

        if (map && marker && newData.lat && newData.lon) {
          const newLatLngArr = [newData.lat, newData.lon];
          marker.setLatLng(newLatLngArr);
          map.panTo(newLatLngArr, { animate: true });

          // Cek apakah kordinat baru ini masuk polygon
          checkPolygonIntersection(newLatLngArr);

          setTimeout(updateCardPosition, 100);
        }
      }
    };

    intervalId = setInterval(doPoll, 20000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadData?.fleet_name, isLoading, isExpired, checkPolygonIntersection]);

  useEffect(() => {
    const layerPath = activeLocationPolygon?.layer?._path;

    if (locPopoverOpen && layerPath) {
      const color = activeLocationPolygon.layer.options?.color || '#ffffff';
      layerPath.style.setProperty('--glow-color', color);
      layerPath.classList.add('polygon-glow-active');
    } else if (layerPath) {
      layerPath.classList.remove('polygon-glow-active');
      layerPath.style.removeProperty('--glow-color');
    }

    return () => {
      if (layerPath) {
        layerPath.classList.remove('polygon-glow-active');
        layerPath.style.removeProperty('--glow-color');
      }
    };
  }, [activeLocationPolygon, locPopoverOpen]);

  const handleBaseMapChange = (mapName) => {
    if (!mapInstanceRef.current) return;
    const map = mapInstanceRef.current;

    Object.values(baseMaps).forEach((layer) => {
      if (map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    });

    if (baseMaps[mapName]) {
      baseMaps[mapName].addTo(map);
    }
    setActiveBaseMap(mapName);
    setIsLayerMenuOpen(false);
  };

  if (isLoading) {
    return (
      <Box sx={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isExpired) {
    return (
      <Box sx={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f1f5f9' }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, maxWidth: 400, textAlign: 'center' }}>
          <ErrorOutlineIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" color="#1e293b" gutterBottom>
            Link Expired
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            This tracking link has exceeded its access time limit. Please contact the administrator or the sender to request a new link.
          </Typography>
          <Button variant="contained" sx={{ bgcolor: '#485D9A', textTransform: 'none' }} onClick={() => navigate('/login')}>
            Login Again
          </Button>
        </Paper>
      </Box>
    );
  }

  if (errorMsg) {
    return (
      <Box sx={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
        <Typography variant="h6" color="error">
          {errorMsg}
        </Typography>
      </Box>
    );
  }

  const { config, canbusData } = payloadData;
  const renderValue = (val, fallback = '-') => (val !== undefined && val !== null && val !== '' ? val : fallback);

  const filteredPolygons = polygonLocations.filter((loc) => {
    const name = (loc.name || loc.pool_name || loc.geofence_name || 'Area').toLowerCase();
    const typeLabel = (loc.location_type === 'Geofence library' ? 'Geofence' : loc.location_type).toLowerCase();
    const search = polygonSearch.toLowerCase();
    const lookupId = loc.company_id || loc.company;

    const matchesSearch = name.includes(search) || typeLabel.includes(search);
    const matchesCompany = !lookupId || selectedCompanies.length === 0 || selectedCompanies.includes(String(lookupId));

    return matchesSearch && matchesCompany;
  });

  const handleCompanyChange = (event) => {
    const {
      target: { value }
    } = event;
    if (value.includes('all')) {
      if (selectedCompanies.length === availableCompanies.length) {
        setSelectedCompanies([]); // Unselect all
      } else {
        setSelectedCompanies(availableCompanies.map((c) => c.id)); // Select all
      }
    } else {
      setSelectedCompanies(typeof value === 'string' ? value.split(',') : value);
    }
  };

  const speed = Number(payloadData.speed || 0);
  const sSpatialMax = Number(payloadData.spatial_max_speed || payloadData.info?.spatial_max_speed || 0);
  const sSpatialMin = Number(payloadData.spatial_min_speed || payloadData.info?.spatial_min_speed || 0);
  const sStatus = String(payloadData.status || '').toLowerCase();
  const sLicense = payloadData.fleet_name || payloadData.vehicle_license_plate || '-';
  const sLocation = payloadData.location || payloadData.last_location?.address || '-';
  const bodyName = payloadData.vehicle_name || payloadData.body_no || '-';

  const msgLower = String(
    payloadData.raw?.speed_message || payloadData.speed_message || payloadData.info?.speed_message || ''
  ).toLowerCase();
  const eventName = String(payloadData.raw?.event || payloadData.event || payloadData.info?.alarm_type || '').toLowerCase();
  const harshEvent = String(payloadData.raw?.harsh_event || payloadData.harsh_event || payloadData.info?.harsh_event || '').toLowerCase();
  const engineStatus = String(
    payloadData.raw?.engine ||
      payloadData.engine ||
      payloadData.raw?.engine_status ||
      payloadData.engine_status ||
      payloadData.info?.engine_status ||
      ''
  ).toUpperCase();
  const limitSpeed = Number(payloadData.vehicle?.max_speed || payloadData.info?.max_speed || 70);

  const isHarshBraking =
    harshEvent.includes('braking') ||
    harshEvent.includes('breaking') ||
    msgLower.includes('harsh braking') ||
    eventName.includes('harsh braking');
  const isHarshAccel =
    harshEvent.includes('acceleration') || msgLower.includes('harsh acceleration') || eventName.includes('harsh acceleration');
  const isHarshCorner = harshEvent.includes('cornering') || msgLower.includes('harsh cornering') || eventName.includes('harsh cornering');

  const isOverSpeedMessage = msgLower.includes('over') || eventName.includes('overspeed');
  const isOverSpeedLimit = engineStatus === 'ON' && speed > limitSpeed;
  const isOverSpeed = isOverSpeedMessage || isOverSpeedLimit || sStatus.includes('over speed') || sStatus.includes('overspeed');

  let speedWarning = null;
  let speedWarningColor = 'error';

  const isLoadedPayload =
    payloadData.raw?.is_in_retase !== false && payloadData.is_in_retase !== false && payloadData.info?.is_in_retase !== false;

  if (isHarshBraking) {
    speedWarning = `${sLicense} Harsh Braking detected in location ${sLocation}`;
    speedWarningColor = 'error';
  } else if (isHarshAccel) {
    speedWarning = `${sLicense} Harsh Acceleration detected in location ${sLocation}`;
    speedWarningColor = 'error';
  } else if (isHarshCorner) {
    speedWarning = `${sLicense} Harsh Cornering detected in location ${sLocation}`;
    speedWarningColor = 'error';
  } else if (sSpatialMax > 0 && speed > sSpatialMax && isLoadedPayload) {
    speedWarning = `${sLicense} Over speed ${speed} km/h in location ${sLocation}, Spatial Max speed : ${sSpatialMax} km/h`;
    speedWarningColor = 'error';
  } else if (sSpatialMin > 0 && speed < sSpatialMin && speed > 0 && isLoadedPayload) {
    speedWarning = `${sLicense} Under speed ${speed} km/h in location ${sLocation}, Spatial Min speed : ${sSpatialMin} km/h`;
    speedWarningColor = 'warning';
  } else if (isOverSpeed) {
    speedWarning = `${sLicense} Over speed ${speed} km/h in location ${sLocation}`;
    speedWarningColor = 'error';
  } else if (sStatus.includes('under speed') || sStatus.includes('underspeed')) {
    speedWarning = `${sLicense} Under speed ${speed} km/h in location ${sLocation}`;
    speedWarningColor = 'warning';
  }

  const engineTemp = canbusData?.canEngineTemp || (canbusData?.canEngineTemperature ? Number(canbusData.canEngineTemperature) / 10 : null);
  const ecuTemp = canbusData?.canEcuTemp || null;
  const hasTempData = Boolean(engineTemp || ecuTemp);

  return (
    <Box sx={{ width: '100vw', height: '100vh', position: 'relative', overflow: 'hidden' }}>
      <Box
        sx={{
          position: 'absolute',
          bottom: 60,
          left: 24,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 1
        }}
      >
        <Tooltip title="Select Base Map" placement="left">
          <IconButton
            onClick={() => setIsLayerMenuOpen(!isLayerMenuOpen)}
            size="small"
            sx={{ color: '#485D9A', bgcolor: '#ffffff', boxShadow: 2 }}
          >
            <LayersIcon />
          </IconButton>
        </Tooltip>

        {isLayerMenuOpen && (
          <Paper elevation={4} sx={{ width: 160, borderRadius: 2, overflow: 'hidden' }}>
            <List disablePadding>
              {Object.keys(baseMaps).map((mapName) => {
                const isActive = mapName === activeBaseMap;
                return (
                  <ListItem
                    key={mapName}
                    button
                    onClick={() => handleBaseMapChange(mapName)}
                    sx={{
                      p: 1,
                      px: 2,
                      bgcolor: isActive ? 'rgba(72, 93, 154, 0.15)' : 'transparent',
                      '&:hover': { bgcolor: 'rgba(72, 93, 154, 0.25)' },
                      borderLeft: isActive ? '4px solid #485D9A' : '4px solid transparent'
                    }}
                  >
                    <ListItemText
                      primary={mapName}
                      primaryTypographyProps={{
                        fontSize: '0.8rem',
                        color: isActive ? '#485D9A' : '#333',
                        fontWeight: isActive ? 'bold' : 'normal'
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        )}
      </Box>

      <Drawer
        variant="persistent"
        anchor="right"
        open={isPolygonSidebarOpen}
        sx={{
          width: 300,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 300,
            boxSizing: 'border-box',
            boxShadow: '4px 0 8px rgba(0,0,0,0.1)',
            borderRight: 'none'
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="bold" color="#1e293b">
              Polygon Areas
            </Typography>
            <IconButton size="small" onClick={() => setIsPolygonSidebarOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <input
            type="text"
            placeholder="Search polygon..."
            value={polygonSearch}
            onChange={(e) => setPolygonSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              outline: 'none',
              fontSize: '14px'
            }}
          />
          {availableCompanies.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Filter Company</InputLabel>
              <Select
                multiple
                value={selectedCompanies}
                onChange={handleCompanyChange}
                renderValue={(selected) => {
                  if (selected.length === availableCompanies.length) return 'All Companies';
                  return selected.map((id) => availableCompanies.find((c) => c.id === id)?.label || id).join(', ');
                }}
                label="Filter Company"
              >
                <MenuItem value="all">
                  <Checkbox
                    checked={selectedCompanies.length > 0 && selectedCompanies.length === availableCompanies.length}
                    indeterminate={selectedCompanies.length > 0 && selectedCompanies.length < availableCompanies.length}
                  />
                  <ListItemText primary="Select All" />
                </MenuItem>
                {availableCompanies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    <Checkbox checked={selectedCompanies.includes(c.id)} />
                    <ListItemText primary={c.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {filteredPolygons.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No polygons found.
            </Typography>
          ) : (
            filteredPolygons.map((loc) => {
              const name = loc.name || loc.pool_name || loc.geofence_name || 'Area';
              const typeLabel = loc.location_type === 'Geofence library' ? 'Geofence' : loc.location_type;
              const companyLabel = loc.join?.company_name || loc.company || 'Unknown Company';
              return (
                <ListItem
                  button
                  key={loc.id}
                  onClick={() => handlePolygonClick(loc)}
                  sx={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {name}
                        </Typography>
                        <Chip
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 'bold'
                          }}
                          label={typeLabel}
                        />
                      </Box>
                    }
                    secondary={
                      <Box component="span">
                        <Typography variant="caption" display="block" color="text.secondary">
                          Company: {companyLabel}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              );
            })
          )}
        </List>
      </Drawer>

      <Box
        ref={mapContainerRef}
        sx={{ width: '100%', height: '100%', mr: isPolygonSidebarOpen ? '300px' : 0, transition: 'margin 0.3s', pt: '48px', zIndex: 1 }}
      />

      {!isPolygonSidebarOpen && (
        <Tooltip title="Show Area" placement="left">
          <IconButton
            onClick={() => setIsPolygonSidebarOpen(true)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              boxShadow: 2,
              zIndex: 1000,
              color: '#485D9A',
              bgcolor: '#ffffff',
              textTransform: 'none'
            }}
            size="small"
          >
            <MapTwoToneIcon />
          </IconButton>
        </Tooltip>
      )}

      {locPopoverOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: 24,
            left: isPolygonSidebarOpen ? '40%' : '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(255,255,255,0.5)',
            p: 2,
            minWidth: 320,
            display: 'flex',
            flexDirection: 'column',
            pt: 3
          }}
        >
          <IconButton size="small" onClick={() => setLocPopoverOpen(false)} sx={{ position: 'absolute', top: 4, right: 4 }}>
            <CloseIcon fontSize="small" />
          </IconButton>

          <Typography variant="subtitle1" fontWeight="bold" color="#485D9A" pr={2}>
            {activeLocationPolygon?.data?.name || activeLocationPolygon?.data?.pool_name || activeLocationPolygon?.data?.geofence_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Company:{' '}
            {activeLocationPolygon?.data?.join?.company_name ||
              activeLocationPolygon?.data?.company?.name ||
              activeLocationPolygon?.data?.company ||
              '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            Tipe:{' '}
            {activeLocationPolygon?.data?.location_type === 'Geofence library' ? 'Geofence' : activeLocationPolygon?.data?.location_type}
          </Typography>
          {(activeLocationPolygon?.data?.max_speed > 0 || activeLocationPolygon?.data?.min_speed > 0) && (
            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
              {activeLocationPolygon?.data?.max_speed > 0 && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Tooltip title="Max Speed">
                    <SpeedIcon fontSize="small" sx={{ color: 'error.main' }} />
                  </Tooltip>
                  <Typography variant="caption" color="error.main" fontWeight="bold">
                    {activeLocationPolygon.data.max_speed} Km/H
                  </Typography>
                </Stack>
              )}
              {activeLocationPolygon?.data?.min_speed > 0 && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Tooltip title="Min Speed">
                    <SpeedIcon fontSize="small" sx={{ color: 'info.main' }} />
                  </Tooltip>
                  <Typography variant="caption" color="info.main" fontWeight="bold">
                    {activeLocationPolygon.data.min_speed} Km/H
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}
        </Box>
      )}

      {showCard && (
        <Box
          sx={{
            position: 'absolute',
            top: cardPosition.top + 150,
            left: cardPosition.left + 200,
            transform: 'translate(-50%, -100%)',
            zIndex: 2000,
            width: 320,
            animation: 'fadeIn 0.2s ease-out forwards',
            '@keyframes fadeIn': {
              '0%': { opacity: 0, transform: 'translate(-50%, -95%) scale(0.95)' },
              '100%': { opacity: 1, transform: 'translate(-50%, -100%) scale(1)' }
            }
          }}
        >
          <Card
            elevation={8}
            sx={{
              width: '100%',
              borderRadius: 3,
              maxHeight: 300,
              overflowY: 'auto',
              pr: 1,
              mb: 2,
              scrollbarWidth: 'thin',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#fff',
              position: 'relative'
            }}
          >
            <Box
              sx={{
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '10px solid transparent',
                borderRight: '10px solid transparent',
                borderTop: '10px solid #fff', // Tail pointing down
                zIndex: 10,
                filter: 'drop-shadow(0px 4px 2px rgba(0,0,0,0.15))'
              }}
            />

            <Box
              sx={{
                p: 2,
                position: 'sticky',
                bgcolor: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                top: 0,
                zIndex: 10,
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={800} color="#1e293b" lineHeight={1.2}>
                  {renderValue(payloadData.fleet_name)}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {renderValue(payloadData.vehicle_type)} • {renderValue(payloadData.imei)}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setShowCard(false)} sx={{ mt: -0.5, mr: -0.5 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', pb: 2 }}>
              <Card variant="outlined" sx={{ borderRadius: 1, p: 1, mb: 0.5, borderColor: '#485D9A' }}>
                {speedWarning && (
                  <Box
                    sx={{
                      p: 1,
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: speedWarningColor === 'error' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(255, 193, 7, 0.1)',
                      border: `1px solid ${speedWarningColor === 'error' ? 'rgba(244, 67, 54, 0.5)' : 'rgba(255, 193, 7, 0.5)'}`,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: speedWarningColor === 'error' ? '#d32f2f' : '#ed6c02', fontWeight: 800, lineHeight: 1.2 }}
                    >
                      ⚠️ {speedWarning}
                    </Typography>
                  </Box>
                )}

                {activePolygonName && (
                  <Box sx={{ p: 1, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #bbdefb', mb: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="primary" fontWeight="bold">
                      📍 Vehicle is in the area: {activePolygonName}
                    </Typography>
                  </Box>
                )}

                <Typography sx={{ fontSize: 12 }}>
                  Fleet
                  <br />
                  <strong>
                    {renderValue(payloadData.fleet_name)} / {bodyName} / {renderValue(payloadData.vehicle_type)}
                  </strong>
                </Typography>

                {config?.showStatusVehicle && (
                  <Grid container justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Status</Typography>
                    <Chip label={getStatusLabel(payloadData)} size="small" variant="outlined" color={getStatusColor(payloadData)} />
                  </Grid>
                )}

                {config?.showVinNo && (
                  <Typography sx={{ fontSize: 12 }}>
                    VIN No <br /> <strong>{renderValue(payloadData.vin)}</strong>
                  </Typography>
                )}

                {hasTempData && (config?.showEngineTemp || config?.showEcuTemp) && (
                  <Stack direction="row" justifyContent="space-between" sx={{ bgcolor: '#f8fafc', px: 1, mt: 0.5, borderRadius: 1 }}>
                    {config?.showEngineTemp && engineTemp && (
                      <Tooltip title="Engine Temp">
                        <Box>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Engine Temp</Typography>
                          <Stack direction="row" alignItems="center" spacing={0.2}>
                            <DeviceThermostatIcon sx={{ fontSize: 14, color: 'error.main' }} />
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">
                              {engineTemp}°C
                            </Typography>
                          </Stack>
                        </Box>
                      </Tooltip>
                    )}
                    {config?.showEcuTemp && ecuTemp && (
                      <Tooltip title="ECU Temp">
                        <Box>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Ecu Temp</Typography>
                          <Stack direction="row" alignItems="center" spacing={0.2} sx={{ ml: 0.5 }}>
                            <MemoryIcon sx={{ fontSize: 14, color: 'info.main' }} />
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">
                              {ecuTemp}°C
                            </Typography>
                          </Stack>
                        </Box>
                      </Tooltip>
                    )}
                  </Stack>
                )}

                {config?.showOdometer && (
                  <Typography sx={{ fontSize: 12 }}>
                    Odometer <br /> <strong>{Number(payloadData.odo || 0)} Km</strong>
                  </Typography>
                )}
                {config?.showDriverName && (
                  <Typography sx={{ fontSize: 12 }}>
                    Driver Name <br />
                    <strong>{renderValue(payloadData.driver_name)}</strong>
                  </Typography>
                )}
              </Card>

              <Card variant="outlined" sx={{ borderRadius: 1, p: 1, borderColor: '#485D9A' }}>
                {config?.showLastLocationTime && (
                  <Typography sx={{ fontSize: 12 }}>
                    Last Location Time
                    <br />
                    <strong>{formatDate(payloadData.last_location_time)}</strong>
                  </Typography>
                )}
                {config?.showLastConnected && (
                  <Typography sx={{ fontSize: 12 }}>
                    Last Connected Time <br />
                    <strong>{formatDate(payloadData.last_connected_time)}</strong>
                  </Typography>
                )}
                {config?.showLastLocation && (
                  <Typography sx={{ fontSize: 12 }}>
                    Last Location <br />
                    <strong>{renderValue(payloadData.last_location?.address || payloadData.location)}</strong>
                  </Typography>
                )}

                {!payloadData.last_location?.is_location_updated && (
                  <Chip
                    icon={<LocationOffIcon sx={{ color: 'red !important' }} />}
                    label="Location is not updated"
                    size="small"
                    sx={{ fontSize: 11, background: '#ffebee', color: '#d32f2f', mt: 0.5, mb: 0.5 }}
                  />
                )}

                <Grid container justifyContent="space-between" alignItems="center">
                  <Grid size={{ xs: 12, md: 6 }}>
                    {config?.showSpeed && (
                      <Typography fontSize={12}>
                        Speed <br /> <strong> {Number(payloadData.speed || 0)} Km/h</strong>
                      </Typography>
                    )}
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    {config?.showSpeedMessage && (
                      <Typography fontSize={12}>
                        Speed Message <br />
                        <strong> {renderValue(payloadData.speed_message || payloadData.info?.speed_message)}</strong>
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                <Grid container justifyContent="space-between" alignItems="center">
                  <Grid size={{ xs: 12, md: 6 }}>
                    {config?.showStopDuration && (
                      <Typography fontSize={12}>
                        Stop Duration <br /> <strong> {renderValue(payloadData.stop_duration)}</strong>
                      </Typography>
                    )}
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    {config?.showEngineStatus && (
                      <Typography fontSize={12}>
                        Engine Status <br />
                        <strong> {getEngineLabel(payloadData.engine_status)}</strong>
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                <Grid container justifyContent="space-between" alignItems="center">
                  <Grid size={{ xs: 12, md: 6 }}>
                    {config?.showTraveled && (
                      <Typography fontSize={12}>
                        Traveled <br /> <strong> {renderValue(payloadData.distance_traveled_km)} Km</strong>
                      </Typography>
                    )}
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    {config?.showTripTime && (
                      <Typography fontSize={12}>
                        Trip Time <br /> <strong>{renderValue(payloadData.trip_time)}</strong>
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                {!hasTempData && !canbusData?.canBatteryPercentage && (
                  <Grid container justifyContent="space-between" alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                      {config?.showEngineTemp && (
                        <Typography fontSize={12}>
                          Temperature <br /> <strong> {engineTemp || '-'} °C </strong>
                        </Typography>
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      {config?.showVoltage && (
                        <Typography fontSize={12}>
                          Voltage <br /> <strong> {renderValue(payloadData.batt_volt)}</strong>
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                )}

                {canbusData?.canBatteryPercentage && (
                  <Grid container justifyContent="space-between" alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                      {config?.showCanBatteryCurrent && (
                        <Typography fontSize={12}>
                          Battery Current <br /> <strong>{Number(canbusData.canBatteryCurrent) / 10000} A</strong>
                        </Typography>
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      {config?.showCanBatteryVolt && (
                        <Typography fontSize={12}>
                          Battery Volt <br /> <strong>{Number(canbusData.canBatteryVolt) / 10} V</strong>
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                )}

                {config?.showTotalWorkEngine && (
                  <Typography fontSize={12}>
                    Total Work Hours <br /> <strong>{renderValue(payloadData.total_work_hours)}</strong>
                  </Typography>
                )}

                <Stack direction="row" spacing={2}>
                  <Box flex={1}>
                    {canbusData?.canBatteryPercentage ? (
                      <Grid container justifyContent="space-between" alignItems="center">
                        <Grid size={{ xs: 12, md: 6 }}>
                          {config?.showBatteryPct && (
                            <Typography fontSize={12}>
                              Battery Percentage <br />
                              <strong> {canbusData.canBatteryPercentage}%</strong>
                            </Typography>
                          )}
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          {config?.showBatteryHealth && (
                            <Typography fontSize={12}>
                              Battery Health <br />
                              <strong>{canbusData.canBatteryHealth}%</strong>
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid container justifyContent="space-between" alignItems="center">
                        <Grid size={{ xs: 12, md: 6 }}>
                          {config?.showFuel && (
                            <Typography fontSize={12}>
                              Fuel <br />{' '}
                              <strong>
                                {renderValue(payloadData.fuel_liter || payloadData.fuel)} L <br />{' '}
                                {renderValue(payloadData.notif_sensor?.canbus_fuel || '-')}
                              </strong>
                            </Typography>
                          )}
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          {config?.showAdBlue && (
                            <Typography fontSize={12}>
                              AdBlue <br />
                              <strong>{renderValue(payloadData.adBlue ?? payloadData.notif_sensor?.adblue ?? '-')} %</strong>
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                </Stack>

                {config?.showImei && (
                  <Box>
                    <Typography fontSize={12}>
                      IMEI <br /> <strong>{renderValue(payloadData.imei)}</strong>
                    </Typography>
                  </Box>
                )}
              </Card>
            </Box>
          </Card>
        </Box>
      )}

      {/* Legend Box */}
      {!isLegendOpen ? (
        <Tooltip title="Open Vehicle Legend" placement="right">
          <IconButton
            onClick={() => setIsLegendOpen(true)}
            sx={{
              position: 'absolute',
              bottom: 24,
              left: 24,
              zIndex: 999,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(8px)',
              '&:hover': { bgcolor: '#fff', transform: 'scale(1.05)' }
            }}
            size="small"
          >
            <InfoOutlinedIcon color="primary" />
          </IconButton>
        </Tooltip>
      ) : (
        <Box
          sx={{
            position: 'absolute',
            bottom: 19,
            left: 19,
            zIndex: 999,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            p: 1.5,
            borderRadius: 2,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            transition: 'left 0.3s ease'
          }}
        >
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                pb: 0.5
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#2c3e50', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                Vehicle Legend
              </Typography>
              <IconButton size="small" onClick={() => setIsLegendOpen(false)} sx={{ p: 0.2 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Grid spacing={2}>
              <Grid container spacing={2} justifyContent="start" alignItems="center" size={{ xs: 12, md: 6 }}>
                <img src={GreenCar} alt="Moving" style={{ width: 50, height: 25, objectFit: 'contain' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                  Moving
                </Typography>
              </Grid>
              <Grid container spacing={2} justifyContent="start" alignItems="center" size={{ xs: 12, md: 6 }}>
                <img src={YellowCar} alt="Idle" style={{ width: 50, height: 25, objectFit: 'contain' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                  Idle
                </Typography>
              </Grid>
              <Grid container spacing={2} justifyContent="start" alignItems="center" size={{ xs: 12, md: 6 }}>
                <img src={RedCar} alt="Delay" style={{ width: 50, height: 25, objectFit: 'contain' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                  Delay
                </Typography>
              </Grid>
              <Grid container spacing={2} justifyContent="start" alignItems="center" size={{ xs: 12, md: 6 }}>
                <img src={PurpleCar} alt="Parked" style={{ width: 50, height: 25, objectFit: 'contain' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                  Parked
                </Typography>
              </Grid>
              <Grid container spacing={2} justifyContent="start" alignItems="center" size={{ xs: 12, md: 6 }}>
                <img src={BlackCar} alt="Stopped" style={{ width: 50, height: 25, objectFit: 'contain' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                  Stopped / Offline
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                mb: 1,
                color: '#2c3e50',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                pb: 0.5,
                fontSize: '0.75rem',
                textTransform: 'uppercase'
              }}
            >
              Warning (Pulse)
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '24px 1fr', gap: '8px 12px', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  border: '2px solid rgba(243, 147, 153, 0.8)',
                  bgcolor: 'rgba(243, 147, 153, 0.3)',
                  justifySelf: 'center'
                }}
              />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                Pink (Over Speed in Area)
              </Typography>

              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  border: '2px solid rgba(244, 67, 54, 0.8)',
                  bgcolor: 'rgba(244, 67, 54, 0.3)',
                  justifySelf: 'center'
                }}
              />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                Red (Over Speed Biasa / Harsh Event)
              </Typography>

              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  border: '2px solid rgba(255, 193, 7, 0.8)',
                  bgcolor: 'rgba(255, 193, 7, 0.3)',
                  justifySelf: 'center'
                }}
              />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                Yellow (Underspeed / Idle)
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
